"use client";

import { styled } from "styled-components";
import { CarCard } from "./carCard";
import { UnstyledList } from "../styled-common/unstyledList";
import { useCarFetcher } from "./fetcher";
import {
  breakpointDesktop,
  breakpointTablet,
  Heading2Text,
  DetailText,
  GhostButton,
  IconButton,
  SingleSelect,
} from "@gnist/design-system";
import { Campaign } from "../campaign/campaign";
import { LoadingCarList } from "@/components/carList/loadingCarList";
import { useDealerCampaign } from "../campaign/campaignProvider";
import { tokens } from "@gnist/themes/tokens.css.js";
import { useMemo, useSyncExternalStore } from "react";
import { sortOptions, SortOption } from "./sorting";
import { useFilters } from "../filters/hooks/useFilters";
import { RangeFilter } from "../filters/types/filters.types";

export const List = styled(UnstyledList)`
  --gap: ${tokens.spacing.s};
  --cols: 3;
  display: flex;
  flex-wrap: wrap;
  gap: var(--gap);
  max-width: 990px;
  justify-content: left;

  @media screen and (max-width: ${breakpointDesktop}) {
    justify-content: center;
  }

  @media screen and (max-width: 1464px) {
    --cols: 2;
  }

  @media screen and (max-width: 560px) {
    --cols: 1;
  }
`;

export const ListItem = styled.li`
  min-width: 250px;
  max-width: 320px;
  flex-basis: calc(
    100% / var(--cols) - var(--gap) / var(--cols) * (var(--cols) - 1)
  );
  display: flex;

  @media screen and (max-width: ${breakpointTablet}) {
    max-width: 348px;
  }
`;

const LoadMoreSection = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: ${tokens.spacing.xl};
  gap: ${tokens.spacing.s};
  align-items: center;
`;

export const NoCarsInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${tokens.spacing.xs};
  align-items: center;
  width: 400px;
  text-align: center;
`;

const StyledGhostButton = styled(GhostButton)`
  min-width: 160px;
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const CampaignWrapper = styled.div`
  max-width: 700px;
  min-width: 100%;
  border-radius: ${tokens.radius.card};
  box-shadow: ${tokens.elevation.low};
  background-color: ${tokens.color.surface};
  display: flex;

  @media screen and (max-width: ${breakpointTablet}) {
    max-width: 348px;
    min-width: unset;
  }
`;

const SortWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${tokens.spacing.s};
  max-width: 990px;

  @media screen and (max-width: ${breakpointDesktop}) {
    justify-content: center;
  }
`;

const StyledSingleSelect = styled(SingleSelect)`
  min-width: 180px;
`;

interface Props {
  departmentIds?: string[];
  departmentSlug?: string;
  dealerName?: string;
  isEmbedded?: boolean;
}

const subscribe = () => () => {};
const getSnapshot = () => true;
const getServerSnapshot = () => false;

export const CarList = ({
  departmentIds,
  departmentSlug,
  dealerName,
  isEmbedded = false,
}: Props) => {
  const isClient = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getServerSnapshot,
  );
  const fetcherResult = useCarFetcher(departmentIds, 15, isEmbedded);
  const {
    cars,
    error,
    isLoading,
    carsCount,
    hasMoreCars,
    sortOption,
    setSortOption,
  } = fetcherResult;

  const { dealerCampaign } = useDealerCampaign(dealerName);

  const {
    brandsWithModels,
    fuelFilter,
    driveWheelFilter,
    priceRange,
    modelYearRange,
    mileageRange,
    departmentsFilter,
    equipmentFilter,
    salesTypeFilter,
    colorFilter,
    resetAllFilters,
    availabilityFilter,
  } = useFilters();

  const hasActiveFilters = useMemo(() => {
    const hasActiveCheckbox = [
      brandsWithModels,
      fuelFilter,
      driveWheelFilter,
      departmentsFilter,
      equipmentFilter,
      salesTypeFilter,
      colorFilter,
      availabilityFilter,
    ].some((filter) => filter?.some((f) => f.isActive));

    const hasActiveBrandModels = brandsWithModels?.some((brand) =>
      brand.models?.some((model) => model.isActive),
    );

    const isRangeActive = (range: RangeFilter | undefined) =>
      range !== undefined && (range.startRange !== 0 || range.endRange !== 0);

    const hasActiveRange =
      isRangeActive(priceRange) ||
      isRangeActive(modelYearRange) ||
      isRangeActive(mileageRange);

    return hasActiveCheckbox || hasActiveBrandModels || hasActiveRange;
  }, [
    brandsWithModels,
    fuelFilter,
    driveWheelFilter,
    priceRange,
    modelYearRange,
    mileageRange,
    departmentsFilter,
    equipmentFilter,
    salesTypeFilter,
    colorFilter,
    availabilityFilter,
  ]);

  if (error) return <div>Klarte ikke laste innhold</div>;

  const carsShown = cars.length;

  const firstThreeCars = cars.slice(0, 3);
  const remainingCars = cars.slice(3);

  if (!isClient || isLoading) {
    return isClient ? <LoadingCarList /> : <div />;
  }

  return (
    <div>
      <SortWrapper>
        <GhostButton
          onClick={resetAllFilters}
          disabled={!hasActiveFilters}
          density="compact"
        >
          Fjern filtre
        </GhostButton>
        <StyledSingleSelect
          label="Sortering"
          hideLabel
          value={sortOption}
          options={sortOptions}
          onChange={(value) => setSortOption(value as SortOption)}
        />
      </SortWrapper>
      <List>
        {firstThreeCars.map((car, i) => (
          <ListItem key={`${car.carId}_${i}`}>
            <CarCard
              car={car}
              departmentSlug={departmentSlug}
              isEmbedded={isEmbedded}
            />
          </ListItem>
        ))}
        {dealerCampaign && carsShown > 0 && (
          <CampaignWrapper>
            <Campaign
              campaign={dealerCampaign}
              departmentSlug={departmentSlug}
            />
          </CampaignWrapper>
        )}
        {remainingCars.map((car, i) => (
          <ListItem key={`${car.carId}_${i}`}>
            <CarCard
              car={car}
              departmentSlug={departmentSlug}
              isEmbedded={isEmbedded}
            />
          </ListItem>
        ))}
      </List>
      <LoadMoreSection>
        {carsShown && !isEmbedded ? (
          <DetailText>
            Viser {carsShown} av {carsCount} biler
          </DetailText>
        ) : (
          !carsShown && (
            <NoCarsInfo>
              <Heading2Text>Fant ingen biler</Heading2Text>
              <p>
                Det finnes dessverre ingen biler med de valgte filtrene eller
                utsalgsstedene.
              </p>
            </NoCarsInfo>
          )
        )}
        {fetcherResult.mode === "paginated" && carsShown > 0 ? (
          <PaginationWrapper>
            {fetcherResult.page > 1 ? (
              <IconButton
                icon="arrow_backward"
                label="Forrige"
                showLabel="right"
                onClick={() => fetcherResult.setPage(fetcherResult.page - 1)}
              />
            ) : (
              <div />
            )}
            <DetailText>
              Side {fetcherResult.page} av{" "}
              {carsCount ? Math.ceil(carsCount / 15) : "?"}
            </DetailText>
            {hasMoreCars ? (
              <IconButton
                icon="arrow_forward"
                label="Neste"
                showLabel="left"
                onClick={() => fetcherResult.setPage(fetcherResult.page + 1)}
              />
            ) : (
              <div />
            )}
          </PaginationWrapper>
        ) : (
          fetcherResult.mode === "infinite" &&
          hasMoreCars && (
            <StyledGhostButton
              onClick={() => fetcherResult.setSize(fetcherResult.size + 1)}
              density="compact"
            >
              Vis flere
            </StyledGhostButton>
          )
        )}
      </LoadMoreSection>
    </div>
  );
};
